import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { playSound } from '../../audio/sounds.js';
import { GameIcon } from '../../components/GameIcon.js';
import { RulesDialog } from '../../components/RulesDialog.js';
import { TetraxStage } from './TetraxStage.js';
import { TETRAX_COUNTDOWN_MS, newGame, type TetraxEvents } from '../../../shared/games/tetrax/index.js';

const BEST_KEY = 'areena.tetraxBest';
const RESTART_DELAY_MS = 1500;
const seed = () => Math.floor(Math.random() * 2 ** 31);

function readBest(): number {
  try {
    return Number(localStorage.getItem(BEST_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Nothing keeps the focus: Space must drop the piece, not press the last clicked button. */
const blurButtons = () => (document.activeElement as HTMLElement | null)?.blur?.();

/**
 * Tetrax alone (user request 2026-10-03): played in the browser, as the snake game alone. No
 * statistics; the best score is kept in this browser.
 */
export function TetraxSolo(props: { onExit: () => void }) {
  const game = useRef(newGame(seed()));
  const [phase, setPhase] = useState<'ready' | 'countdown' | 'playing' | 'paused' | 'over'>('ready');
  const [count, setCount] = useState(0);
  const [best, setBest] = useState(readBest);
  const [newBest, setNewBest] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const endedAt = useRef(0);

  const start = () => {
    game.current = newGame(seed());
    setNewBest(false);
    setPhase('countdown');
    setCount(Math.round(TETRAX_COUNTDOWN_MS / 1000));
    blurButtons();
  };

  // Space or Enter starts a new game; P or Esc continues after a pause.
  useEffect(() => {
    if (phase !== 'ready' && phase !== 'over' && phase !== 'paused') return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (phase === 'paused') {
        if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') setPhase('playing');
        return;
      }
      // Right after the end the drop key does not start a new game at once.
      if (phase === 'over' && Date.now() - endedAt.current < RESTART_DELAY_MS) return;
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        start();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [phase]);

  // 3–2–1.
  useEffect(() => {
    if (phase !== 'countdown') return;
    if (count <= 0) {
      setPhase('playing');
      return;
    }
    playSound('snakeCount');
    const timer = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [phase, count]);

  const onEvents = (e: TetraxEvents) => {
    if (!e.over) return;
    const score = game.current.score;
    if (score > readBest()) {
      try {
        localStorage.setItem(BEST_KEY, String(score));
      } catch {
        // Not stored (private window).
      }
      setBest(score);
      setNewBest(score > 0);
    }
    endedAt.current = Date.now();
    setPhase('over');
  };

  const s = game.current;
  const overlay =
    phase === 'countdown' ? (
      <div class="tetrax-big">{count > 0 ? String(count) : t.tetrax.go}</div>
    ) : phase === 'paused' ? (
      <div class="tetrax-big tetrax-small">{t.tetrax.paused}</div>
    ) : phase === 'ready' || phase === 'over' ? (
      <div class="tetrax-menu">
        {phase === 'over' && <div class="tetrax-menu-title">{newBest ? t.tetrax.newBest : t.tetrax.toppedOut}</div>}
        {phase === 'over' && <div>{t.tetrax.soloOver(s.score, s.lines)}</div>}
        <button class="btn btn-primary" onClick={start} autofocus>
          {phase === 'over' ? t.tetrax.soloAgain : t.tetrax.soloStart}
        </button>
        <div class="tetrax-menu-hint">{t.tetrax.soloHint}</div>
      </div>
    ) : null;

  return (
    <section class="game-view">
      <div class="game-head">
        <h2>
          <GameIcon game="tetrax" size={30} /> {t.tetrax.soloTitle}
        </h2>
        <button class="btn btn-small" onClick={() => setShowRules(true)}>
          📖 {t.rules.buttonShort}
        </button>
        <button class="btn btn-small" onClick={props.onExit}>
          {t.snake.back}
        </button>
      </div>
      <div class="tetrax">
        <TetraxStage
          state={game}
          active={phase === 'playing'}
          dim={phase !== 'playing'}
          overlay={overlay}
          onEvents={onEvents}
          onPause={() => setPhase('paused')}
          extra={
            <dl class="tetrax-stats">
              <dt>{t.tetrax.best}</dt>
              <dd>{best.toLocaleString('fi-FI')}</dd>
            </dl>
          }
        />
        <p class="tetrax-keys muted">{t.tetrax.keys}</p>
      </div>
      {showRules && <RulesDialog game="tetrax" onClose={() => setShowRules(false)} />}
    </section>
  );
}
